import React, { useState, useEffect } from 'react';
import api from './api';
import './styles/index.css';
import DashboardHeader from './DashboardHeader';

const PlayerHome = ({ 
  onGoToLigas, 
  onGoToTorneos, 
  onGoToTraining,
  onGoToOnline,
  onGoToMatchHistory,
  onLogout 
}) => {
  const [user] = useState(api.getCurrentUser());
  const [stats, setStats] = useState(() => {
    // Intentar recuperar stats del sessionStorage para evitar flash al volver
    const saved = sessionStorage.getItem('playerHomeStats');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) { /* ignore */ }
    }
    return {
      promedioTotal: 0,
      porcentajeVictorias: 0,
      totalLigas: 0,
      promedioOnline: null,
      porcentajeVictoriasOnline: null
    };
  });
  const [loading, setLoading] = useState(() => {
    // Solo mostrar loading si no tenemos stats previos guardados
    return !sessionStorage.getItem('playerHomeStats');
  });

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStats = async () => {
    try {
      // Solo mostrar loading si no hay datos previos (primera carga)
      const hasExistingData = stats.promedioTotal > 0 || stats.totalLigas > 0;
      if (!hasExistingData) {
        setLoading(true);
      }
      
      const [promedioData, ligas, onlineStats] = await Promise.all([
        api.getPromedioTotal(),
        api.getMyLeagues(),
        api.getOnlineStats()
      ]);
      
      const newStats = {
        promedioTotal: promedioData.promedioTotal || 0,
        porcentajeVictorias: promedioData.porcentajeVictorias || 0,
        totalLigas: ligas.length,
        promedioOnline: onlineStats?.totalPartidas > 0 ? (onlineStats.promedioOnline ?? null) : null,
        porcentajeVictoriasOnline: onlineStats?.totalPartidas > 0 ? (onlineStats.porcentajeVictorias ?? null) : null
      };
      
      setStats(newStats);
      // Guardar en sessionStorage para acceso instantáneo al volver
      sessionStorage.setItem('playerHomeStats', JSON.stringify(newStats));
    } catch (err) {
      console.error('Error cargando estadísticas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('playerHomeStats');
    api.logout();
    if (onLogout) onLogout();
    window.location.reload();
  };

  return (
    <div className="dart-league-dashboard">
      <DashboardHeader
        user={user}
        subtitle="Panel de Jugador"
        loading={loading}
        promedioTotal={stats.promedioTotal}
        porcentajeVictorias={stats.porcentajeVictorias}
        promedioOnline={stats.promedioOnline}
        porcentajeVictoriasOnline={stats.porcentajeVictoriasOnline}
        onLogout={handleLogout}
      />

      {/* Main Content */}
      <main className="player-home-main">

        {/* Bloque único de estadísticas */}
        <section className="player-stats-panel">
          <div className="player-stats-panel-header">
            <span className="player-stats-panel-icon">📊</span>
            <span className="player-stats-panel-title">Mis Estadísticas</span>
          </div>
          <div className="player-stats-panel-grid">
            <div className="player-stats-panel-item">
              <span className="panel-item-emoji">🎯</span>
              <span className="panel-item-value">{loading ? '-' : stats.promedioTotal.toFixed(1)}</span>
              <span className="panel-item-label">Media Presencial</span>
            </div>
            <div className="player-stats-panel-item online">
              <span className="panel-item-emoji">🌐</span>
              <span className="panel-item-value">{loading ? '-' : (stats.promedioOnline != null ? stats.promedioOnline.toFixed(1) : '-')}</span>
              <span className="panel-item-label">Media Online</span>
            </div>
            <div className="player-stats-panel-item">
              <span className="panel-item-emoji">🏆</span>
              <span className="panel-item-value">{loading ? '-' : `${stats.porcentajeVictorias.toFixed(0)}%`}</span>
              <span className="panel-item-label">Vic. Presencial</span>
            </div>
            <div className="player-stats-panel-item online-wins">
              <span className="panel-item-emoji">🏅</span>
              <span className="panel-item-value">{loading ? '-' : (stats.porcentajeVictoriasOnline != null ? `${stats.porcentajeVictoriasOnline.toFixed(0)}%` : '-')}</span>
              <span className="panel-item-label">Vic. Online</span>
            </div>
          </div>
        </section>

        {/* Título de sección */}
        <h3 className="player-section-title">¿Qué quieres hacer?</h3>

        {/* Action Cards */}
        <section className="player-action-grid">
          {/* Ver Mis Ligas */}
          <button className="player-action-card ligas" onClick={onGoToLigas}>
            <div className="player-action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="7"></circle>
                <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"></polyline>
              </svg>
            </div>
            <div className="player-action-content">
              <h4>Mis Ligas</h4>
              <p>Clasificación, jornadas y partidas</p>
              {!loading && stats.totalLigas > 0 && (
                <span className="player-action-badge">{stats.totalLigas} liga{stats.totalLigas !== 1 ? 's' : ''} activa{stats.totalLigas !== 1 ? 's' : ''}</span>
              )}
            </div>
            <div className="player-action-arrow">→</div>
          </button>

          {/* Ver Campeonatos */}
          <button className="player-action-card torneos" onClick={onGoToTorneos}>
            <div className="player-action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path>
                <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path>
                <path d="M4 22h16"></path>
                <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"></path>
                <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"></path>
                <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"></path>
              </svg>
            </div>
            <div className="player-action-content">
              <h4>Campeonatos</h4>
              <p>Torneos y brackets activos</p>
              <span className="player-action-badge">🏅 Compite y gana</span>
            </div>
            <div className="player-action-arrow">→</div>
          </button>

          {/* Entrenamientos */}
          <button className="player-action-card entrenamiento" onClick={onGoToTraining}>
            <div className="player-action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <circle cx="12" cy="12" r="6"></circle>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>
            </div>
            <div className="player-action-content">
              <h4>Entrenamientos</h4>
              <p>Practica tus cierres y mejora</p>
              <span className="player-action-badge">🎯 1, 2 y 3 dardos</span>
            </div>
            <div className="player-action-arrow">→</div>
          </button>

          {/* Jugar Online */}
          <button className="player-action-card online" onClick={onGoToOnline}>
            <div className="player-action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
            </div>
            <div className="player-action-content">
              <h4>Jugar Online</h4>
              <p>Entra en una sala y juega con otros jugadores</p>
              <span className="online-pulse-badge"><span className="online-pulse-dot"></span> Sala abierta</span>
            </div>
            <div className="player-action-arrow">→</div>
          </button>

          {/* Historial de Partidas Online */}
          <button className="player-action-card historial" onClick={onGoToMatchHistory}>
            <div className="player-action-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
            </div>
            <div className="player-action-content">
              <h4>Historial Online</h4>
              <p>Tus partidas, resultados y estadísticas</p>
              {!loading && stats.promedioOnline != null && (
                <span className="player-action-badge">🎯 Media: {stats.promedioOnline.toFixed(1)}</span>
              )}
            </div>
            <div className="player-action-arrow">→</div>
          </button>
        </section>

        {/* Tips con Glow */}
        <section className="player-tips">
          <div className="player-tip-box">
            <div className="player-tip-icon">💡</div>
            <p className="player-tip-text"><strong>Consejo:</strong> Practica los cierres de 2 dardos regularmente para mejorar tu promedio en partidas reales.</p>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PlayerHome;
